'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { staticUrl } from '../lib/config/static-assets';
import { rewriteCatalogCdnUrl } from '../lib/config/cdn';

const live2dResourceBase = () => rewriteCatalogCdnUrl('https://cdnimg-v2.gamekee.com/wiki4.0/bangdream/catalog_8d4fbe12ff61416bbf1d8691fe899fa3/live2d/');

type Model = { guid: string; name: string; character_id: number; model3_site_path: string; motions: number; costume_ids: number[] };
let modelIndex: Promise<Model[]> | undefined;
function loadModels(): Promise<Model[]> {
  return modelIndex ??= fetch(`${live2dResourceBase()}data/live2d-index.json`)
    .then(async response => {
      if (!response.ok) throw new Error('Model index unavailable');
      const data = await response.json() as { records?: Model[] };
      if (!Array.isArray(data.records)) throw new Error('Invalid model index');
      return data.records.filter((model: Model) => /^[a-f0-9]{32}$/.test(model.guid)
        && model.model3_site_path === `assets/models/${model.guid}/model.model3.json`);
    }).catch(error => { modelIndex = undefined; throw error; });
}

function costumeLabel(model: Model) {
  const name = model.name;
  if (name.includes('_child_')) return '幼年服装';
  if (name.includes('_school_summer_')) return '夏季校服';
  if (name.includes('_school_winter_jhs')) return '初中校服';
  if (name.includes('_school_winter_')) return '冬季校服';
  if (name.includes('_casual_')) return '日常服装';
  if (name.includes('_mask')) return '演出服 · 面具';
  if (name.includes('_low')) return '舞台服装 · 简化版';
  if (name.includes('_live-live2d_')) return '舞台服装';
  if (name.includes('_live_')) return '演出服';
  return model.costume_ids.length ? `服装 ${model.costume_ids[0]}` : '其他服装';
}

function ModelFrame({ guid, playing, onState }: { guid: string; playing: boolean; onState: (state: 'ready' | 'error') => void }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [src, setSrc] = useState('');
  useEffect(() => {
    const url = new URL(staticUrl('/vendor/cubism/player.html'), window.location.origin);
    url.searchParams.set('origin', window.location.origin);
    url.searchParams.set('model', guid);
    url.searchParams.set('resources', live2dResourceBase());
    setSrc(url.href);
  }, [guid]);
  useEffect(() => {
    if (!src) return;
    const origin = new URL(src).origin;
    const sendPlayback = () => frame.current?.contentWindow?.postMessage({ type: 'bangdream-cubism-playback', playing }, origin);
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow || event.origin !== origin
        || event.data?.type !== 'bangdream-cubism' || event.data.guid !== guid) return;
      if (event.data.state === 'ready' || event.data.state === 'error') onState(event.data.state);
      if (event.data.state === 'ready') sendPlayback();
    };
    window.addEventListener('message', handleMessage);
    sendPlayback();
    return () => window.removeEventListener('message', handleMessage);
  }, [src, guid, playing, onState]);
  useEffect(() => {
    if (!src || !playing) return;
    const origin = new URL(src).origin;
    let pending = 0;
    let point = { x: 0, y: 0 };
    const send = (x: number, y: number, active: boolean) => frame.current?.contentWindow?.postMessage({ type: 'bangdream-cubism-pointer', x, y, active }, origin);
    const move = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return;
      const bounds = frame.current?.getBoundingClientRect();
      if (!bounds) return;
      const centerX = bounds.left + bounds.width / 2;
      const centerY = bounds.top + bounds.height * .28;
      const radiusX = Math.max(120, bounds.width * .32);
      const radiusY = Math.max(100, bounds.height * .32);
      point = {
        x: Math.max(-1, Math.min(1, (event.clientX - centerX) / radiusX)),
        y: Math.max(-1, Math.min(1, (centerY - event.clientY) / radiusY)),
      };
      if (!pending) pending = requestAnimationFrame(() => { pending = 0; send(point.x, point.y, true); });
    };
    const reset = () => { if (pending) cancelAnimationFrame(pending); pending = 0; send(0, 0, false); };
    const leave = (event: MouseEvent) => { if (!event.relatedTarget) reset(); };
    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('mouseout', leave);
    window.addEventListener('blur', reset);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('mouseout', leave);
      window.removeEventListener('blur', reset);
      reset();
    };
  }, [src, playing]);
  return src ? <>
    <iframe ref={frame} src={src} title="角色 Live2D" tabIndex={-1} sandbox="allow-scripts allow-same-origin" />
    <button type="button" className="bangdream-cubism-interact" aria-label="点击角色播放动作" onClick={() => frame.current?.contentWindow?.postMessage({ type: 'bangdream-cubism-interact' }, new URL(src).origin)} />
  </> : null;
}

export default function BangdreamLive2D({ characterId, fallback, name, playing }: {
  characterId: number; fallback: string; name: string; playing: boolean;
}) {
  const [models, setModels] = useState<Model[]>([]);
  const [selected, setSelected] = useState('');
  const lastLive2D = useRef('');
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [indexLoading, setIndexLoading] = useState(true);
  const [indexError, setIndexError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [costumeOpen, setCostumeOpen] = useState(false);
  const costumeTools = useRef<HTMLDivElement>(null);
  const [mobileAnchor, setMobileAnchor] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const media = window.matchMedia('(max-width: 760px)');
    const update = () => setMobileAnchor(media.matches ? document.getElementById('bangdream-costume-mobile-anchor') : null);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    let active = true;
    setSelected('');
    lastLive2D.current = '';
    setModels([]);
    setIndexLoading(true);
    setIndexError(false);
    setCostumeOpen(false);
    loadModels().then(records => {
      if (!active) return;
      const matches = records.filter(model => model.character_id === characterId);
      setModels(matches);
      const initial = matches.find(model => model.name.includes('_casual_') && !model.name.includes('_child_'))
        || matches.find(model => model.costume_ids.length && model.motions > 9)
        || matches[0];
      setSelected(initial?.guid || '');
      lastLive2D.current = initial?.guid || '';
      setStatus('loading');
      setIndexLoading(false);
    }).catch(() => { if (active) { setIndexError(true); setIndexLoading(false); } });
    return () => { active = false; };
  }, [characterId, attempt]);
  useEffect(() => {
    if (!costumeOpen) return;
    const dismiss = (event: PointerEvent) => {
      if (!costumeTools.current?.contains(event.target as Node)) setCostumeOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCostumeOpen(false);
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, [costumeOpen]);
  useLayoutEffect(() => {
    if (!costumeOpen || !mobileAnchor) return;
    const resizePanel = () => {
      const tools = costumeTools.current;
      const panel = tools?.querySelector<HTMLElement>('.bangdream-costume-panel');
      if (!tools || !panel) return;
      const navBottom = document.querySelector('.bangdream-detail-shell .v2-nav')?.getBoundingClientRect().bottom ?? 0;
      const spaceAbove = tools.getBoundingClientRect().top - Math.max(navBottom, 16) - 20;
      if (spaceAbove < 140) {
        panel.style.position = 'fixed';
        panel.style.top = `${Math.max(navBottom + 8, 16)}px`;
        panel.style.bottom = '16px';
        panel.style.left = '14px';
        panel.style.right = '14px';
        panel.style.width = 'auto';
        panel.style.maxHeight = 'none';
      } else {
        panel.style.position = '';
        panel.style.top = '';
        panel.style.bottom = '';
        panel.style.left = '';
        panel.style.right = '';
        panel.style.width = '';
        panel.style.maxHeight = `${Math.min(window.innerHeight * .5, spaceAbove)}px`;
      }
    };
    resizePanel();
    window.addEventListener('resize', resizePanel);
    return () => window.removeEventListener('resize', resizePanel);
  }, [costumeOpen, mobileAnchor]);
  function choose(guid: string) {
    if (guid) lastLive2D.current = guid;
    else if (selected) lastLive2D.current = selected;
    if (guid !== selected) { setStatus('loading'); setSelected(guid); }
    setCostumeOpen(false);
  }
  const counts = new Map<string, number>();
  const options = models.map(model => {
    const label = costumeLabel(model);
    const count = (counts.get(label) || 0) + 1;
    counts.set(label, count);
    return { model, label: count === 1 ? label : `${label} ${count}` };
  });
  const controls = <div ref={costumeTools} className="bangdream-costume-tools" role="group" aria-label="切换服装">
    <button type="button" className="bangdream-costume-option" aria-pressed={!selected} onClick={() => choose(selected ? '' : lastLive2D.current || models[0]?.guid || '')}>静态立绘</button>
    <button type="button" className="bangdream-costume-option" aria-expanded={costumeOpen} aria-controls="bangdream-costume-panel" onClick={() => setCostumeOpen(open => !open)}>切换时装</button>
    {costumeOpen && <div id="bangdream-costume-panel" className="bangdream-costume-panel" role="group" aria-label="选择 Live2D 服装">
      <div className="bangdream-costume-panel-title">选择 Live2D 服装</div>
      {options.length ? <div className="bangdream-costume-panel-list">
        {options.map(({ model, label }) => <button key={model.guid} type="button" aria-pressed={selected === model.guid} onClick={() => choose(model.guid)}>{label}</button>)}
      </div> : <p>{indexError ? '服装列表加载失败' : '正在读取服装…'}</p>}
    </div>}
    {selected && status === 'error' && <small role="status">服装加载失败，已显示立绘<button type="button" onClick={() => setAttempt(value => value + 1)}>重试</button></small>}
  </div>;
  return <>
    <img src={fallback} alt={name} style={{ visibility: indexLoading || (selected && status !== 'error') ? 'hidden' : 'visible' }} />
    {(indexLoading || (selected && status === 'loading')) && <div className="bangdream-cubism-loading" role="status">Live2D 加载中…</div>}
    {selected && <div className={`bangdream-cubism-stage${status === 'ready' ? ' is-ready' : ''}`}>
      <ModelFrame key={selected} guid={selected} playing={playing} onState={setStatus} />
    </div>}
    {mobileAnchor ? createPortal(controls, mobileAnchor) : controls}
    {indexError && <div className="bangdream-costume-error" role="status">服装列表加载失败。<button type="button" onClick={() => setAttempt(value => value + 1)}>重新加载</button></div>}
  </>;
}
